import axios from 'axios';

// Normalize the API base URL to prevent duplicates
const normalizeApiBase = (baseUrl) => {
  if (!baseUrl) return '/api';

  // Remove trailing slashes
  let normalized = baseUrl.replace(/\/+$/, '');

  // Ensure it ends with /api but doesn't have duplicate /api
  if (!normalized.endsWith('/api')) {
    if (normalized.includes('/api/')) {
      normalized = normalized.replace(/\/api\/.*$/, '/api');
    } else {
      normalized += '/api';
    }
  }

  return normalized;
};

const API_BASE = normalizeApiBase(process.env.REACT_APP_API_BASE_URL);

const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  withCredentials: true,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Ensure paths don't start with /api if baseURL already has it
    if (config.url?.startsWith('/api') && config.baseURL?.endsWith('/api')) {
      config.url = config.url.replace(/^\/api/, '');
    }

    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const { response } = error;

    // Handle specific error cases
    if (response) {
      switch (response.status) {
        case 401:
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          break;
        case 403:
          window.location.href = '/forbidden';
          break;
        case 404:
          // Handle not found errors
          break;
        case 500:
          // Handle server errors
          break;
        default:
          break;
      }
    }

    // Return a consistent error format
    return Promise.reject({
      status: response?.status || 0,
      message: error.message || 'Network Error',
      data: response?.data,
    });
  }
);

// API methods with path normalization
export const secureGet = (path, params = {}) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return apiClient.get(normalizedPath, { params });
};

export const securePost = (path, data, config = {}) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return apiClient.post(normalizedPath, data, config);
};

export const securePut = (path, data) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return apiClient.put(normalizedPath, data);
};

export const secureDelete = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return apiClient.delete(normalizedPath);
};

// Additional utility methods
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Usage example:
// In your component:
// import { secureGet, securePost, setAuthToken } from './apiClient';
//
// // Set token after login:
// setAuthToken(response.data.token);
//
// // Make API calls:
// const data = await secureGet('/cart');
// await securePost('/cart/add', { productId: 123 });